<template>
  <div>
    <v-card
      class="white--text"
      color="blue-grey darken-2"
    >
      <v-card-title
        class="pa-6"
        primary-title
      >
        <div class="text-h5">{{ $t('Summary') }}</div>
        <v-spacer />
        <v-tooltip
          :disabled="isMini"
          top
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :disabled="$wait.is('fetching receipt')"
              class="mr-1"
              min-width="50"
              v-on="on"
              @click="toggleNoteDialog"
            >
              <v-icon
                :left="isMini"
                small
              >fal fa-sticky-note</v-icon>
              <span v-show="isMini">{{ $t('Note') }}</span>
              <v-badge
                v-if="receipt.note"
                :offset-x="0"
                :offset-y="-5"
                bordered
                class="badge-discount"
                color="primary"
                icon="far fa-check"
              />
            </v-btn>
          </template>
          <span>{{ $t('Note') }}</span>
        </v-tooltip>
        <v-tooltip
          :disabled="isMini"
          top
        >
          <template v-slot:activator="{ on }">
            <v-btn
              :disabled="$wait.is('fetching receipt')"
              min-width="50"
              v-on="on"
              @click="toggleDiscountDialog"
            >
              <v-icon
                :left="isMini"
                small
              >fal fa-badge-percent</v-icon>
              <span v-show="isMini">{{ $t('Receipt Discount') }}</span>
              <v-badge
                v-if="receiptHasDiscount"
                :offset-x="0"
                :offset-y="-5"
                bordered
                class="badge-discount"
                color="primary"
                icon="far fa-check"
              />
            </v-btn>
          </template>
          <span>{{ $t('Receipt Discount') }}</span>
        </v-tooltip>
      </v-card-title>
      <v-list
        class="blue-grey darken-1 pt-0"
        dark
        dense
        style="position:relative"
        two-line
      >
        <v-progress-linear
          v-show="$wait.is('changing customer')"
          :indeterminate="true"
          class="mt-0"
          height="3"
        />
        <v-list-item
          :disabled="$wait.is('fetching receipt')"
          @click="toggleCustomersDialog"
        >
          <v-list-item-avatar height="30px">
            <v-icon>fal fa-user-tag</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>{{ _.get(receipt, 'customer.fullName', $t('Walk In')) }}</v-list-item-title>
            <v-list-item-subtitle>{{ _.get(receipt, 'customer.email') }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <div>
              <v-btn
                :disabled="$wait.is('fetching receipt')"
                class="mr-1 black--text"
                color="white"
                ripple
                small
              >
                <v-icon
                  class="mr-2"
                  small
                >fal fa-search</v-icon>
                {{ $t('Search {0}', [$t('Customer')]) }}
              </v-btn>

              <v-tooltip
                v-if="receipt.customer"
                color="blue-grey"
                left
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    ripple
                    small
                    v-on="on"
                    @click.stop="onCustomerSelect(null)"
                  >
                    <v-icon
                      color="grey lighten-1"
                      small
                    >far fa-trash-alt</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Select {0}', [$t('Guest')]) }}</span>
              </v-tooltip>
            </div>
          </v-list-item-action>
        </v-list-item>
        <v-divider />
      </v-list>
      <v-slide-y-transition>
        <v-list
          v-show="receipt.note"
          id="nota-list"
          class="blue-grey darken-1 mt-n4"
          dark
          dense
        >
          <v-list-item @click="toggleNoteDialog">
            <v-list-item-content>
              <h4 class="subheading mt-0">{{ $t('Staff Note') }}</h4>
            </v-list-item-content>
            <v-list-item-content class="justify-end text-right pr-1">
              <p class="mb-0"><em v-html="receipt.note" /></p>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
        </v-list>
      </v-slide-y-transition>
      <v-list
        class="blue-grey darken-1 super-dense"
        dark
      >
        <v-list-item>
          <v-list-item-content>{{ $t('Subtotal') }}</v-list-item-content>
          <v-list-item-content
            v-show="!$wait.is('fetching receipt')"
            class="justify-end"
          >
            <!-- {{ receipt.totalProductsInclVat + (-receipt.totalDiscountInclVat) || 0 | money }} -->
            {{ receipt.totalOriginalProductsInclVat || 0 | money }}
          </v-list-item-content>
          <v-list-item-action class="ml-1 justify-end" />
        </v-list-item>
        <v-slide-y-transition>
          <v-list-item
            v-show="receipt.totalDiscountInclVat"
            transition="scale-transition"
          >
            <v-list-item-content>
              <template v-if="receipt.totalDiscountInclVat > 0">{{ $t('Addition') }}</template>
              <template v-else>{{ $t('Discount') }}</template>
            </v-list-item-content>
            <v-list-item-content
              v-show="!$wait.is('fetching receipt')"
              class="justify-end"
            >
              {{ receipt.roundedTotalDiscountInclVat || 0 | money }}
            </v-list-item-content>
            <v-list-item-action class="ml-0 justify-end">
              <v-tooltip
                color="blue-grey"
                left
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="ma-0"
                    dark
                    icon
                    ripple
                    small
                    v-on="on"
                    @click="removeDiscounts"
                  >
                    <v-icon small>far fa-trash-alt</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Remove {0}', [$t('Discount')]) }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-slide-y-transition>
        <v-list-item>
          <v-list-item-content>{{ $t('VAT') }}</v-list-item-content>
          <v-list-item-content
            v-show="!$wait.is('fetching receipt')"
            class="justify-end"
          >
            {{ receipt.totalVat || 0 | money }}
          </v-list-item-content>
          <v-list-item-action class="ml-1 justify-end" />
        </v-list-item>
        <v-divider class="mt-4 mb-1" />
      </v-list>

      <v-list
        class="blue-grey darken-1 pt-0"
        dark
        two-line
      >
        <v-list-item class="pt-2 pb-2">
          <v-list-item-content class="align-end text-h4">
            <v-btn
              :loading="$wait.is('handling payment button') || $wait.is('fetching receipt')"
              :color="checkoutBtnColor"
              :disabled="!itemCount"
              block
              class="btnPayment elevation-3"
              large
              @click="handlePaymentButton"
            >
              <v-row class="align-end justify-center">
                <v-col class="btnPaymentAmount text-left ml-2">
                  <v-icon
                    class="mr-6 mt-n2"
                    dark
                    left
                    size="34px"
                  >fal fa-shopping-basket</v-icon>
                  <template v-if="!receipt.totalInclVat || receipt.totalInclVat >= 0">{{ $t('Checkout') }}</template>
                  <template v-else>{{ $t('Refund') }}</template>
                </v-col>
                <v-col class="btnPaymentAmount text-right ml-2">
                  <template>{{ receipt.totalInclVatRounded || 0 | money }}</template>
                </v-col>
              </v-row>
            </v-btn>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-row
        class="pl-4 pr-4 pt-2 pb-2"
        wrap
      >
        <v-col
          class="pa-0 pl-1 pr-1"
          cols="6"
        >
          <v-btn
            :disabled="!itemCount || $wait.is('fetching receipt')"
            block
            color="white"
            depressed
            large
            @click="parkReceipt"
          >
            <v-icon
              dark
              left
              size="20px"
            >fal fa-parking</v-icon> {{ $t('Park Receipt') }}
          </v-btn>
        </v-col>
        <v-col
          class="pa-0 pl-1 pr-1"
          cols="6"
        >
          <v-btn
            :disabled="!itemCount || $wait.is('fetching receipt')"
            block
            color="white"
            depressed
            large
            @click="clearProducts"
          >
            <v-icon
              left
              dark
              size="20px"
            >fal fa-trash-alt</v-icon> {{ $t('Clear') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <v-dialog
      v-model="showNoteDialog"
      max-width="450"
    >
      <forms-notes-save
        ref="form-note"
        :focus-input="showNoteDialog"
        :form-title="$t('Note')"
        :receipt-object="receipt"
        @saved="toggleNoteDialog"
      />
    </v-dialog>

    <v-dialog
      v-model="showDiscountDialog"
      :max-width="450"
      eager
    >
      <forms-discounts-receipt-save
        v-if="showDiscountDialog"
        ref="form-receipt-discount"
        :clear-input="showDiscountDialog"
        :focus-input="showDiscountDialog"
        :form-title="$t('Receipt Discount')"
        :receipt-object="receipt"
        keyboard-class="keyboardReceiptDiscount"
        @saved="toggleDiscountDialog"
      />
    </v-dialog>

    <select-customer-dialog
      :show-dialog="showCustomersDialog"
      @closed="toggleCustomersDialog"
      @item-saved="onCustomerSelect($event.uuid)"
      @item-selected="onCustomerSelect($event.uuid)"
    />
  </div>
</template>

<script>
import FormsNotesSave from '@/components/forms/notes/FormsNotesSave'
import ProductsTable from '@/components/products/ProductsTable'
import FormsDiscountsReceiptSave from '@/components/forms/discounts/FormsDiscountsReceiptSave'
import BarcodeScanner from '@/components/BarcodeScanner'
import SelectCustomerDialog from '@/components/dialogs/SelectCustomerDialog'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'CartSummary',

  components: {
    FormsNotesSave,
    FormsDiscountsReceiptSave,
    ProductsTable,
    BarcodeScanner,
    SelectCustomerDialog
  },

  data () {
    return {
      showCustomersDialog: false,
      showNoteDialog: false,
      showDiscountDialog: false,
      isLoading: false,
      checkoutBtnColor: 'secondary darken-1',
      isCheckoutBtnActive: false,
      parkedName: null
    }
  },

  computed: {
    ...mapState('RECEIPT', ['receipt']),
    ...mapState(['isMini']),
    ...mapGetters('RECEIPT', ['products', 'itemCount']),
    receiptHasDiscount () {
      return this.receipt.discountType && this.receipt.discountValue !== null
    }
  },
  watch: {
    showNoteDialog (val) {
      this.$emit('cart-blurred', val)
    },
    showCustomersDialog (val) {
      this.$emit('cart-blurred', val)
    },
    showDiscountDialog (val) {
      this.$emit('cart-blurred', val)
      // reset keyboard memory when keyboard popup opened
      if (val) {
        // this.$refs['form-receipt-discount'].$refs['keyboard'].clear()
      }
    }
  },

  created () {
    // fetch the cart
    this.$store.dispatch('RECEIPT/FETCH_RECEIPT')
      .then((data) => {
        const transactions = _.get(data, 'receipt.transactions')
        // show payment dialog immediately if receipt has transactions
        if (transactions && transactions.length) {
          this.handlePaymentButton()
        }
      })
      .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
  },

  methods: {
    onCustomerSelect (data) {
      if (data) this.toggleCustomersDialog()
      // save customer_uuid to receipt
      return this.$store.dispatch('RECEIPT/CHANGE_CUSTOMER', { customerUuid: data })
        .then(data => this.helpers.toastSuccess(data.message))
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
    },
    removeDiscounts () {
      this.$store.dispatch('RECEIPT/REMOVE_DISCOUNTS')
    },
    toggleCustomersDialog () {
      this.showCustomersDialog = !this.showCustomersDialog
    },
    toggleNoteDialog () {
      this.showNoteDialog = !this.showNoteDialog
    },
    toggleDiscountDialog () {
      this.showDiscountDialog = !this.showDiscountDialog
    },
    handlePaymentButton () {
      this.$wait.start('handling payment button')
      return this.$store.dispatch('RECEIPT/CHECK_REFUND_NEEDED')
        .then(() => {
          this.$emit('paying')
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => this.$wait.end('handling payment button'))
    },
    clearProducts () {
      this.$swal({
        title: this.$t('Are you sure?'),
        type: 'warning',
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Yes'),
        showCancelButton: true,
        reverseButtons: true
      })
        .then((result) => {
          if (result.value) {
            return this.$store.dispatch('RECEIPT/CLEAR_PRODUCTS')
              .then(data => this.helpers.toastSuccess(data.message))
              .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
          }
        })
    },
    parkReceipt () {
      this.$swal({
        input: 'text',
        inputValue: moment().format('DD/MM/YYYY HH:mm'),
        inputPlaceholder: this.$t('Enter a {0}', [this.$t('Name').toLowerCase()]),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Save'),
        allowOutsideClick: false,
        reverseButtons: true,
        inputValidator: (value) => {
          return new Promise((resolve) => {
            if (value.length > 3) {
              this.parkedName = value
              resolve()
            } else {
              resolve(this.$t('You need to type something here!'))
            }
          })
        }
      })
        .then((result) => {
          // if a value has been typed
          if (result.value) {
            this.$wait.start('loading page')
            return this.$store.dispatch('RECEIPT/PARK_RECEIPT', result.value)
              .then(data => this.helpers.toastSuccess(data.message))
              .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
              .finally(() => this.$wait.end('loading page'))
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
#top-menu-actions /deep/ .v-list__item__avatar {
  min-width: 40px;
}

#nota-list:hover {
  cursor: pointer;
}

#nota-list /deep/ .v-list__item {
  height: auto;
}

#nota-list /deep/ .v-list__item__action {
  min-width: 25px;
}

.v-list__item__action {
  min-width: 35px;
}

.btnPayment {
  height: auto !important;
  padding: 1.5em 2em !important;
}

.btnPaymentAmount {
  font-size: 26px;
}

.theme--light.v-btn.v-btn--disabled:not(.v-btn--icon):not(.v-btn--text):not(.v-btn--outline) {
  background-color: #f5f5f5 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/checkout/CartSummary.vue